import React, { useEffect, useRef } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useFilters,
  useGlobalFilter,
} from "react-table";

import { Table, Pagination, Row, Col, Form, OverlayTrigger, Tooltip, Button } from "react-bootstrap";
import generateExcel from "zipcelx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faSort,
  faSortUp,
  faSortDown,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { HelpTooltip, SelectColumnFilter } from "../assets/functions.js";

function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter }, gotoPage
}) {
  const [value, setValue] = React.useState(filterValue ?? "");
  const gotoPageRef = React.useRef(gotoPage);
  gotoPageRef.current = gotoPage;
  const setFilterRef = React.useRef(setFilter);
  setFilterRef.current = setFilter;

  // Synchronize internal state when filterValue changes externally (e.g. resetTrigger)
  React.useEffect(() => {
    setValue(filterValue ?? "");
  }, [filterValue]);

  // Debounce calling setFilter and gotoPage(0)
  React.useEffect(() => {
    if (value === (filterValue ?? "")) return;

    const timeoutId = setTimeout(() => {
      gotoPageRef.current?.(0);
      setFilterRef.current?.(value || undefined);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [value, filterValue]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      gotoPageRef.current?.(0);
      setFilterRef.current?.(value || undefined);
    }
  };

  return (
    <Form.Control
      type="input"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={``}
      className="mt-2"
    />
  );
}

export { SelectColumnFilter };

const getComparableDate = (value) => {
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  if (value === null || value === undefined) return null;

  const normalizedValue = String(value).trim();
  if (!normalizedValue) return null;

  const brazilianDateMatch = normalizedValue.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(?:às\s*)?(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/i
  );

  if (brazilianDateMatch) {
    const [, day, month, year, hour = "0", minute = "0", second = "0"] = brazilianDateMatch;
    const parsedDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );

    if (
      parsedDate.getFullYear() === Number(year) &&
      parsedDate.getMonth() === Number(month) - 1 &&
      parsedDate.getDate() === Number(day)
    ) {
      return parsedDate.getTime();
    }
  }

  const databaseDateMatch = normalizedValue.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?(?:Z|[+-]\d{2}:?\d{2})?$/
  );

  if (databaseDateMatch) {
    const timestamp = Date.parse(normalizedValue.includes("T") ? normalizedValue : normalizedValue.replace(" ", "T"));
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  return null;
};

const comparePrimitiveValues = (valueA, valueB) => {
  const emptyA = valueA === null || valueA === undefined || valueA === "";
  const emptyB = valueB === null || valueB === undefined || valueB === "";

  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;

  const numberA = typeof valueA === "number" ? valueA : Number(String(valueA).replace(",", "."));
  const numberB = typeof valueB === "number" ? valueB : Number(String(valueB).replace(",", "."));

  if (!Number.isNaN(numberA) && !Number.isNaN(numberB)) {
    return numberA === numberB ? 0 : numberA > numberB ? 1 : -1;
  }

  const dateA = getComparableDate(valueA);
  const dateB = getComparableDate(valueB);

  if (dateA !== null && dateB !== null) {
    return dateA === dateB ? 0 : dateA > dateB ? 1 : -1;
  }

  return String(valueA).localeCompare(String(valueB), "pt-BR", {
    numeric: true,
    sensitivity: "base",
  });
};

export const buildExcelExportMetadataRows = (exportMetadata = []) => (
  (Array.isArray(exportMetadata) ? exportMetadata : [])
    .map((item) => ({
      label: String(item?.label ?? "").trim(),
      value: String(item?.value ?? "").trim(),
    }))
    .filter((item) => item.label || item.value)
    .map((item) => ([
      { value: item.label, type: "string" },
      { value: item.value, type: "string" },
    ]))
);

export const getExcelCell = (value) => {
  if (value === null || value === undefined) {
    return { value: "", type: "string" };
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return { value, type: "number" };
  }

  if (value instanceof Date) {
    return { value: value.toISOString(), type: "string" };
  }

  if (React.isValidElement(value)) {
    return { value: "", type: "string" };
  }

  if (typeof value === "object") {
    try {
      return {
        value: JSON.stringify(value) || "",
        type: "string",
      };
    } catch {
      return {
        value: String(value),
        type: "string",
      };
    }
  }

  return {
    value: String(value),
    type: "string",
  };
};

export const ColumnSortingTable = ({
  columns,
  data,
  getCellProps,
  getRowProps,
  title,
  setTriggerExport,
  triggerExport,
  removeCard,
  onFilteredDataChange,
  onPageChange,
  onPaginationChange,
  initialState,
  resetTrigger,
  pageResetTrigger,
  tableCountTooltip,
  exportMetadata = [],
  manualPagination = false,
  serverPageCount = 1,
  totalRows
}) => {
  // Helper function to normalize text - removes accents and special characters
  const normalizeText = (text) => {
    if (text === null || text === undefined) return '';
    return String(text)
      .normalize('NFD') // Decompose characters with accents
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
      .toLowerCase()
      .trim();
  };

  const isCellEmpty = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string" && value.trim() === "") return true;
    if (Array.isArray(value)) {
      return value.length === 0 || value.every((item) => isCellEmpty(item));
    }
    return false;
  };

  const filterTypes = React.useMemo(
    () => ({
      // Text filter with accent normalization - "a" matches "á", "à", "â", "ã", etc.
      text: (rows, id, filterValue) => {
        if (!filterValue) return rows;

        if (filterValue === "__EMPTY__") {
          return rows.filter((row) => isCellEmpty(row.values[id]));
        }
        
        const normalizedFilter = normalizeText(filterValue);
        
        return rows.filter((row) => {
          const rowValue = row.values[id];
          const normalizedRowValue = normalizeText(rowValue);
          return normalizedRowValue.includes(normalizedFilter);
        });
      },
      // Exact-like filter used by select inputs; supports array-valued cells and empty/null values.
      equals: (rows, id, filterValue) => {
        if (filterValue === undefined || filterValue === null || filterValue === "") {
          return rows;
        }

        if (filterValue === "__EMPTY__") {
          return rows.filter((row) => isCellEmpty(row.values[id]));
        }

        const normalizedFilter = normalizeText(filterValue);

        return rows.filter((row) => {
          const rowValue = row.values[id];

          if (Array.isArray(rowValue)) {
            return rowValue.some((item) => normalizeText(item) === normalizedFilter);
          }

          return normalizeText(rowValue) === normalizedFilter;
        });
      },
    }),
    []
  );

  const sortTypes = React.useMemo(
    () => ({
      smart: (rowA, rowB, columnId) =>
        comparePrimitiveValues(rowA.values[columnId], rowB.values[columnId]),
    }),
    []
  );

  const sanitizedColumns = React.useMemo(() => {
    if (!Array.isArray(columns)) return [];
    return columns.map((col) => {
      if (!col) return col;
      const clean = { ...col };
      if ("Cell" in clean && typeof clean.Cell === "undefined") {
        delete clean.Cell;
      }
      if ("Header" in clean && typeof clean.Header === "undefined") {
        delete clean.Header;
      }
      const header = clean.Header;
      const hasVisibleHeader = header === undefined
        ? Boolean(clean.accessor || clean.id)
        : header !== null && !(typeof header === "string" && header.trim() === "");

      if (hasVisibleHeader) {
        if (clean.Filter === false) delete clean.Filter;
        if (clean.disableFilters) delete clean.disableFilters;
      }

      return clean;
    });
  }, [columns]);

  const defaultColumn = React.useMemo(
    () => ({
      Cell: ({ value = "" }) =>
        typeof value === "boolean" ? String(value) : (value ?? ""),
      Filter: ({ column, gotoPage }) => (
        <DefaultColumnFilter column={column} gotoPage={gotoPage} />
      ),
      filter: 'text', // Set default filter type to use our custom text filter
      sortType: 'smart',
    }),
    []
  );

  const {
    allColumns,
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setAllFilters,
    setGlobalFilter,
    setSortBy,
    setHiddenColumns,
    state: { pageIndex, pageSize, sortBy, hiddenColumns },
  } = useTable(
    {
      columns: sanitizedColumns,
      data,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      sortTypes,
      initialState: initialState || { pageIndex: 0 },
      sortable: false,
      autoResetPage: false,
      autoResetFilters: false,
      autoResetSortBy: false,
      manualPagination,
      pageCount: manualPagination ? Math.max(1, Number(serverPageCount) || 1) : undefined,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const defaultHiddenColumnsRef = useRef(
    Array.isArray(initialState?.hiddenColumns) ? [...initialState.hiddenColumns] : []
  );
  const defaultHiddenColumns = defaultHiddenColumnsRef.current;
  const hasNonDefaultColumnVisibility =
    hiddenColumns.length !== defaultHiddenColumns.length ||
    hiddenColumns.some((columnId) => !defaultHiddenColumns.includes(columnId));

  // Reset filters and pagination when resetTrigger changes
  useEffect(() => {
    if (resetTrigger !== undefined && resetTrigger !== null) {
      setAllFilters([]);
      setGlobalFilter(undefined);
      gotoPage(0);
    }
  }, [resetTrigger, setAllFilters, setGlobalFilter, gotoPage]);

  const activeColumnIds = JSON.stringify(allColumns.map((column) => column.id));
  const sortByRef = useRef(sortBy);
  sortByRef.current = sortBy;
  useEffect(() => {
    if (pageResetTrigger === undefined || pageResetTrigger === null) return;
    const ids = new Set(JSON.parse(activeColumnIds));
    setAllFilters((current) => current.filter((filter) => ids.has(filter.id)));
    const nextSortBy = sortByRef.current.filter((sort) => ids.has(sort.id));
    if (nextSortBy.length !== sortByRef.current.length) setSortBy(nextSortBy);
    gotoPage(0);
  }, [pageResetTrigger, activeColumnIds, setAllFilters, setSortBy, gotoPage]);

  function renderHeader(column) {
    const rendered =
      typeof column.render === "function" &&
      (typeof column.Header === "function" ||
        typeof column.Header === "object" ||
        typeof column.Header === "string")
        ? column.render("Header")
        : column.Header;

    return (
      <>
        {rendered}
        {column.tooltip && (
          <OverlayTrigger overlay={<Tooltip>{column.tooltip}</Tooltip>}>
            <span
              className="ms-1 d-inline-block"
              onClick={(e) => e.stopPropagation()}
            >
              <HelpTooltip icon={faExclamationCircle} />
            </span>
          </OverlayTrigger>
        )}
      </>
    );
  }

  function isExportableColumn(column) {
    if (column.disableExport === true) return false;
    if (column.exportValue || column.exportAccessor || column.exportHeader) return true;
    if (column.id === "icons" || column.id === "select") return false;

    const hasTextHeader =
      column.Header !== undefined &&
      column.Header !== null &&
      !React.isValidElement(column.Header) &&
      String(column.Header).trim() !== "";

    return hasTextHeader;
  }

  function getExportHeader(column) {
    if (column.exportHeader !== undefined && column.exportHeader !== null) {
      return String(column.exportHeader);
    }

    if (
      column.Header !== undefined &&
      column.Header !== null &&
      !React.isValidElement(column.Header)
    ) {
      return String(column.Header);
    }

    return column.id || "";
  }

  function getExportValue(row, column) {
    if (typeof column.exportValue === "function") {
      return column.exportValue(row.original, row.values[column.id], row);
    }

    if (column.exportAccessor) {
      return row.original?.[column.exportAccessor];
    }

    return row.values[column.id];
  }

  function getExcel() {
    const config = {
      filename: `${title} - Histórico`,
      sheet: {
        data: [],
      },
    };

    const dataSet = config.sheet.data;

    const exportColumns = headerGroups.length
      ? headerGroups[headerGroups.length - 1].headers.filter(isExportableColumn)
      : [];

    const metadataRows = buildExcelExportMetadataRows(exportMetadata);

    metadataRows.forEach((item) => {
      dataSet.push(item);
    });

    if (metadataRows.length > 0) {
      dataSet.push([]);
    }

    dataSet.push(
      exportColumns.map((column) => ({
        value: getExportHeader(column),
        type: "string",
      }))
    );

    if (rows.length > 0) {
      rows.forEach((row) => {
        const dataRow = [];
        exportColumns.forEach((column) => {
          dataRow.push(getExcelCell(getExportValue(row, column)));
        });
        dataSet.push(dataRow);
      });
    }
    return generateExcel(config);
  }

  useEffect(() => {
    if (triggerExport) {
      getExcel();
      setTriggerExport(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerExport]);

  const onFilteredDataChangeRef = useRef(onFilteredDataChange);
  onFilteredDataChangeRef.current = onFilteredDataChange;
  const prevFilteredRowsRef = useRef();

  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;
  const prevPageRowsRef = useRef();

  const onPaginationChangeRef = useRef(onPaginationChange);
  onPaginationChangeRef.current = onPaginationChange;
  const prevPaginationRef = useRef({ pageIndex: -1, pageSize: -1 });

  // Expose filtered data when it changes
  useEffect(() => {
    if (!onFilteredDataChangeRef.current || !rows) return;
    const filteredData = rows.map((row) => row.original);
    const prev = prevFilteredRowsRef.current;
    const isSame =
      prev &&
      prev.length === filteredData.length &&
      prev.every((item, i) => item === filteredData[i]);

    if (!isSame) {
      prevFilteredRowsRef.current = filteredData;
      onFilteredDataChangeRef.current(filteredData);
    }
  }, [rows]);

  // Notify parent of current page rows whenever page/pageSize changes
  useEffect(() => {
    if (!onPageChangeRef.current || !page) return;
    const currentPageData = page.map((row) => row.original);
    const prev = prevPageRowsRef.current;
    const isSame =
      prev &&
      prev.length === currentPageData.length &&
      prev.every((item, i) => item === currentPageData[i]);

    if (!isSame) {
      prevPageRowsRef.current = currentPageData;
      onPageChangeRef.current(currentPageData);
    }
  }, [pageIndex, pageSize, page]);

  useEffect(() => {
    if (!onPaginationChangeRef.current) return;
    if (
      prevPaginationRef.current.pageIndex !== pageIndex ||
      prevPaginationRef.current.pageSize !== pageSize
    ) {
      prevPaginationRef.current = { pageIndex, pageSize };
      onPaginationChangeRef.current({ pageIndex, pageSize });
    }
  }, [pageIndex, pageSize]);

  return (
    <div className={`${removeCard ? '' : 'card'}`}>
      <div className={`${removeCard ? '' : 'card-body'}`}>
        <div className="d-flex align-items-center justify-content-end gap-2 mb-1">
          {hasNonDefaultColumnVisibility && (
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              onClick={() => setHiddenColumns([...defaultHiddenColumns])}
            >
              Restaurar colunas
            </Button>
          )}
          <p className="mb-0 text-muted">
            Mostrando {manualPagination && Number.isFinite(Number(totalRows)) ? Number(totalRows) : rows.length} itens
            {tableCountTooltip && (
              <OverlayTrigger overlay={<Tooltip>{tableCountTooltip}</Tooltip>}>
                <span className="ms-1 d-inline-block">
                  <HelpTooltip icon={faExclamationCircle} />
                </span>
              </OverlayTrigger>
            )}
          </p>
        </div>
        <div className="overflow-auto">
          <Table striped bordered {...getTableProps()} className="column-sorting-table">
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key: headerGroupKey, ...headerGroupProps } =
                  headerGroup.getHeaderGroupProps();

                return (
                  <tr key={headerGroupKey} {...headerGroupProps}>
                    {headerGroup.headers.map((column) => {
                      const { key: columnKey, ...columnProps } = column.getHeaderProps();

                      return (
                        <th key={columnKey} {...columnProps}>
                          <div
                            className={`d-flex align-items-center justify-content-between ${column.canSort ? "user-select-none" : ""}`}
                            style={column.canSort ? { cursor: "pointer" } : undefined}
                            onClick={column.canSort ? () => column.toggleSortBy() : undefined}
                            role={column.canSort ? "button" : undefined}
                            tabIndex={column.canSort ? 0 : undefined}
                            onKeyDown={column.canSort ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                column.toggleSortBy();
                              }
                            } : undefined}
                          >
                            <span>{renderHeader(column)}</span>
                            <span className="d-inline-flex align-items-center ms-2 text-nowrap">
                              {column.canSort && (
                                <span>
                                  {column.isSorted ? (
                                    column.isSortedDesc ? (
                                      <FontAwesomeIcon icon={faSortUp} />
                                    ) : (
                                      <FontAwesomeIcon icon={faSortDown} />
                                    )
                                  ) : (
                                    <FontAwesomeIcon icon={faSort} className="text-muted" />
                                  )}
                                </span>
                              )}
                              {!column.columns?.length && (
                                <FontAwesomeIcon
                                  icon={faTimes}
                                  className="ms-2 text-muted"
                                  style={{ cursor: "pointer" }}
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`Ocultar coluna ${column.id}`}
                                  title={`Ocultar coluna ${column.id}`}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    column.toggleHidden(true);
                                  }}
                                  onKeyDown={(event) => {
                                    event.stopPropagation();
                                    if (event.key === "Enter" || event.key === " ") {
                                      event.preventDefault();
                                      column.toggleHidden(true);
                                    }
                                  }}
                                />
                              )}
                            </span>
                          </div>
                          <div>
                            {column.canFilter ? column.render("Filter", { gotoPage }) : null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.length > 0 ? (
                page.map((row) => {
                  prepareRow(row);
                  const extraRowProps = getRowProps ? getRowProps(row) : {};
                  const { key: rowKey, ...rowProps } = row.getRowProps(extraRowProps);

                  return (
                    <tr key={rowKey} {...rowProps}>
                      {row.cells.map((cell) => {
                        const { key: cellKey, ...cellProps } = cell.getCellProps([
                          {},
                          getCellProps ? getCellProps(cell) : {},
                        ]);

                        return (
                          <td key={cellKey} {...cellProps}>
                            {typeof cell.render === "function" &&
                            (typeof cell.column?.Cell === "function" ||
                              typeof cell.column?.Cell === "object")
                              ? cell.render("Cell")
                              : typeof cell.value === "boolean"
                              ? String(cell.value)
                              : cell.value ?? ""}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                // Display a message or a row when there are no data
                <tr>
                  <td className="py-4" colSpan={sanitizedColumns.length} style={{ textAlign: "center" }}>
                    Não foi encontrado nenhum resultado
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        <Row className="text-start mt-2">
          <Col md="6">
            <span className="mx-2">
              Página{" "}
              <span>
                {pageIndex + 1} de {pageOptions.length}
              </span>
            </span>
            <span className="ms-3 me-2">Mostrar:</span>
            <Form.Select
              className="d-inline-block w-auto"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md="6">
            <Pagination className="float-end mb-0">
              <Pagination.First
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              />
              <Pagination.Prev
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              />
              <Pagination.Next
                onClick={() => nextPage()}
                disabled={!canNextPage}
              />
              <Pagination.Last
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              />
            </Pagination>
          </Col>
        </Row>
      </div>
    </div>
  );
};
